/* eslint-disable no-shadow */
import {PaginationData} from '_components/admin/admin.types';
import {
  Prescriber,
  PrescribersDictionary,
} from '_redux/admin/prescribers/admin-prescriber-model.types';
import {
  Therapist,
  TherapistsDictionary,
} from '_redux/admin/therapists/admin-therapist-model.types';
import {
  CancelAppointmentFailureAction,
  CancelAppointmentStartAction,
  CancelAppointmentSuccessAction,
  ExportAppointmentsCsvFailureAction,
  ExportAppointmentsCsvStartAction,
  ExportAppointmentsCsvSuccessAction,
  FetchAllAppointmentsFailureAction,
  FetchAllAppointmentsStartAction,
  FetchAllAppointmentsSuccessAction,
  FetchAppointmentActivityLogsFailureAction,
  FetchAppointmentActivityLogsStartAction,
  FetchAppointmentActivityLogsSuccessAction,
  FetchPaginatedClientsFailureAction,
  FetchPaginatedClientsStartAction,
  FetchPaginatedClientsSuccessAction,
  FetchPaginatedProvidersFailureAction,
  FetchPaginatedProvidersStartAction,
  FetchPaginatedProvidersSuccessAction,
  FetchProviderAvailabilityFailureAction,
  FetchProviderAvailabilityStartAction,
  FetchProviderAvailabilitySuccessAction,
  ResetAppointmentActivityLogsAction,
  ResetAppointmentsStateAction,
  ResetClientsAvailability,
  ResetFilters,
  ResetProviderAvailabilityAction,
  ResetSelectedAppointmentIdAction,
  ResetSpecificDaysAppointmentsStateAction,
  ScheduleAppointmentFailureAction,
  ScheduleAppointmentStartAction,
  ScheduleAppointmentSuccessAction,
  SetProviderDetailsAction,
  SetSelectedAppointmentIdAction,
  UpdateAppointmentFailureAction,
  UpdateAppointmentStartAction,
  UpdateAppointmentSuccessAction,
  UpdateFetchedProvidersAction,
} from './calendar-actions.types';

export type Provider = Therapist | Prescriber;

export type AppointmentLabels = {
  id: number;
  name: string;
  color: string;
};

export enum UserRoles {
  admin = 'admin',
  mentalhealthcoach = 'mentalhealthcoach',
  therapist = 'therapist',
  prescriber = 'prescriber',
  member = 'patient',
}

export type ProviderRole = UserRoles.therapist | UserRoles.prescriber;

export enum ChargeStatus {
  charged = 'charged',
  scheduled = 'scheduled',
  systemFailure = 'system-failure',
  clientFailure = 'client-failure',
  notCharged = 'not-charged',
}

export enum PaymentService {
  STRIPE = 'stripe',
  PAYPAL = 'paypal',
  PAYSTACK = 'paystack',
}

type ParticipantActivities = {
  email: string;
  role: string;
  activity: string;
  activityTime: Date;
};

type SessionActivities = {
  activity: string;
  activityTime: Date;
};
export type CountryOfResidenceType = {
  code: string;
  name: string;
};

export type Meeting = {
  patientId: string;
  meetingName: string;
  providerId: string;
  providerRole: UserRoles;
  startDate: Date;
  endDate: Date;
  roomUrl: string;
  roomMode: string;
  meetingId: string;
  hostRoomUrl: string;
  appointmentID: number;
  appointmentLabel: {
    label: string;
    value: number;
  };
  appointmentCharge: number;
  appointmentChargeCurrency: string;
  chargeStatus: ChargeStatus;
  participantActivities: ParticipantActivities[];
  sessionActivities: SessionActivities[];
  createdAt: string;
  updatedAt: string;
  chargeError?: {
    errorCode: string;
    paymentMethod: PaymentService;
    message: string;
  };
  paymentRequestedAt?: string;
};

export type WherebyRoomModeTypes = 'normal' | 'group';

export interface Appointment {
  appointmentID: string;
  appointmentTypeId: number;
  firstName: string;
  lastName: string;
  email: string;
  dateOfAppointment: string; // e.g. "September 16, 2025"
  timeOfAppointment: string; // e.g. "2:00am"
  endTime: string; // e.g. "2:30am"
  datetime?: string; // e.g. "2024-09-16T14:00:00-04:00" (Full ISO datetime in provider's timezone)
  calendar: string;
  calendarID: number;
  timeZone: string; // e.g. "Europe/Madrid" (Provider's timezone)
  patientTimezone?: string; // e.g. "America/Los_Angeles" (Patient's timezone)
  duration: number | string; // minutes (payload shows "30" as a string)
  canceled: boolean;
  labels: {
    id: number;
    name: string;
    color: string;
  }[];
  classID: string | null;
  category: string; // e.g. "Mental Health Counseling Video-Call"
  type: string; // e.g. "Video-Call with Therapist"
  patientId: string;

  patientDetails: {
    _id: string;
    fullName: string;
    email: string;
    doctorId: string;
    therapist_Id: string; // note the underscore in the source
    patientId: string;
    role: string;
    accountType: string;
    countryOfResidence: {
      name: string; // e.g. "Spain"
      code: string; // e.g. "ES"
    };
    digitalPracticeAccountType: string;
    stateOfResidence: string;
  };

  meeting: {
    _id: string;
    calcomBookingUid: string;
    __v: number;
    canceled: boolean;
    chargeStatus: string;
    createdAt: string; // ISO timestamp
    roomMode: string;
    sessionActivities: unknown[];
    updatedAt: string; // ISO timestamp
    appointmentID: string;
    appointmentId: string; // both appear in payload
  };

  providerId: string;
  providerDetails: {
    _id: string;
    acuity: {
      calendarId: number;
      timezone: string;
    };
    countryOfResidence: {
      name: string; // e.g. "Spain"
      code: string; // e.g. "ES"
    };
    email: string;
    fullName: string;
    image: string; // URL
    role: string;
    states: string[];
    stateOfResidence: string;
    calendar: {
      cal: {
        eventTypeIds: {
          id: number;
          title: string;
        }[];
      };
    };
  };
}

export type ResponseTypeAppointment = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  datetime: string;
  date: string;
  time: string;
  endTime: string;
  datetimeCreated: string;
  dateCreated: string;
  type: string;
  appointmentTypeID: number;
  title: string;
  category: string;
  duration: number;
  calendar: string;
  calendarID: number;
  timezone: string;
  calendarTimezone: string;
  forms: any[];
  labels: {
    id: number;
    name: string;
    color: string;
  }[];
  canceled: boolean;
  canClientCancel: boolean;
  classID: string | null;
  providerName: string;
  participantCount: number;
  label: string;
  appointmentTypeSlug: string;
  status: string;
  source: string;
  slug: string;
  patientTimezone: string;
};

export type ApiSpecificPaginationData = {
  currentPage: number;
  totalItems: number;
  hasNextPage: boolean;
  pageSize: number;
};

export type QueryKeysType = {
  searchTerm: string;
  providerId: string;
  from: string;
  to: string;
  country: string;
  timezone: string;
};

type GroupedCollection = {
  appointments: Appointment[];
  pagination: ApiSpecificPaginationData;
  queryKeys: QueryKeysType;
};

export type AppointmentMap = Record<string, GroupedCollection>;

export type AvailabilityInfoSlot = {
  availabilityDate: string;
  availabilityTimes: string[];
};

export type SlotsType = {
  [providerId: string]: AvailabilityInfoSlot[];
};

export enum ProvidersModelTypes {
  prescribers = 'prescribers',
  therapists = 'therapists',
}

export type SetProviderDetailsPayload = {
  providerModelType: ProvidersModelTypes;
  providerId: string;
};

export type PaginatedModels<T> = {
  pagination?: PaginationData;
  entities: T;
};

export type ProviderEntityTypes = {
  therapists?: TherapistsDictionary;
  prescribers?: PrescribersDictionary;
};

export type ModelsByIdState = {
  [ProvidersModelTypes.therapists]?: PaginatedModels<TherapistsDictionary>;
  [ProvidersModelTypes.prescribers]?: PaginatedModels<PrescribersDictionary>;
};

export type AvailabilityInfoState = {
  providerDetails?: Provider;
  appointmentTypeId: string;
  appointmentTypeName: string;
  category: string;
  slots: SlotsType;
  providers: ModelsByIdState;
  isFetchingProvider: boolean;
};

export type FetchPaginatedProvidersResponse = PaginationData & {
  results: Therapist[] | Prescriber[];
};

export type FetchProviderAvailabilityPayload = {
  providerId: string;
  appointmentType: string;
  eventTypeTitle: string;
  yearAndMonth: string;
  duration: string;
  calendarId?: string;
  state?: string;
  callback?: (data: {appointmentTypeId?: string}) => void;
};

export type AvailabilitySlotsResponse = {
  providerId: string;
  appointmentTypeId: string;
  appointmentTypeName: string;
  category: string;
  slots: AvailabilityInfoSlot[];
};

export type FetchPaginatedProvidersStartPayload = {
  providerModel: ProvidersModelTypes;
  countryCode?: string | undefined;
  searchKeywords: string;
  page: number;
  maxResults: number;
  callback?: (value: boolean) => void;
};

export type NormalizedFetchedTherapists = {
  entities: {
    therapists: TherapistsDictionary;
  };
  pagination?: PaginationData;
  result: string[];
  filter: ProvidersModelTypes;
};

export type NormalizedFetchedPrescribers = {
  entities: {
    prescribers: PrescribersDictionary;
  };
  pagination?: PaginationData;
  result: string[];
  filter: ProvidersModelTypes;
};

export type NormalizedFetchedProviders = {
  entities: {
    therapists: TherapistsDictionary;
    prescribers: PrescribersDictionary;
  };
  pagination?: PaginationData;
  result: string[];
  filter: ProvidersModelTypes;
};

// fetch paginated clients
export type FetchPaginatedClientsPayload = {
  search: string;
  page: number;
  maxResults: number;
  countryCode?: string;
  callback?: () => void;
};

export type Client = {
  date: number;
  patientId: string;
  fullName: string;
  email: string;
  calendar: {
    cal: {
      timeZone: string;
    };
  };
  phoneNumber: string;
  stateOfResidence: string;
  countryOfResidence: {
    name: string;
    code: string;
  };
};

export type ClientsDictionary = {
  [key: string]: Client;
};

export type FetchedAllProvidersResponse = {
  results: Therapist[] | Prescriber[];
  pagination: PaginationData;
};

export type FetchedClientsResponse = {
  results: Client[];
  pagination: PaginationData;
};

export type ClientsAvailabilityState = {
  pagination?: PaginationData;
  clientsList: ClientsDictionary;
  isLoading: boolean;
};

// fetch all appointments
export type LoadingStatusMap = Record<string, boolean>;
export type AppointmentsStateInfo = {
  collections: Appointment[];
  groupedCollections: AppointmentMap;
  selectedAppointmentId: string;
  pagination: {
    currentPage: number;
    totalItems: number;
    hasNextPage: boolean;
    pageSize: number;
  };
  isLoadingAppointments: boolean;
  queryKeys: QueryKeysType;
  loadingStatusMap: LoadingStatusMap;
  lastResetAt: number | null;
};
// query/payload
export type FetchAllAppointmentsPayload = {
  searchTerm: string;
  from: string; // dateString;
  to: string; // dateString;
  providerId: string; // prescriberId | therapistId;
  maxItems: number;
  page: number;
  filter?: boolean;
  country: string;
  timezone: string;
  callback?: () => void;
};
// response
export type FetchAllAppointmentsResponse = {
  appointments: Appointment[];
  // groupedAppointments: AppointmentMap;
  filter: boolean;
  queryKeys?: QueryKeysType;
  pagination: ApiSpecificPaginationData;
};

// schedule appointment
// query/payload
export type ScheduleAppointmentPayload = {
  appointmentDateTimeString: string;
  prescriberId: string;
  therapistId: string;
  patientFullName: string;
  patientEmail: string;
  patientPhoneNumber: string;
  patientCountry: string;
  patientTimezone: string;
  appointmentTypeID: number;
  patientState: string;
  appointmentType: string;
  eventTypeTitle?: string;
  callback?: () => void;
};

export type BookingFormPayload = {
  providerCountry: string;
  providerState: string;
  providerId: string;
  patientFullName: string;
  patientEmail: string;
  patientPhoneNumber: string;
  patientTimezone: string;
  patientCountry: string;
  patientState: string; // default to single appointment
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: string;
  providerType: string;
  providerTimezone: string;
  appointmentTypeId: number | undefined;
  callback?: () => void;
};
// response
export type ScheduleAppointmentResponse = Appointment;

// cancel appointment
// query/payload
export type CancelAppointmentPayload = {
  appointmentID: string;
  reasonForCancellation: string;
  callback?: () => void;
};
// response
export type CancelAppointmentResponse = ResponseTypeAppointment;

// update appointment
// query/payload
export type UpdateAppointmentPayload = {
  appointmentId: string;
  labelId: number;
  callback?: () => void;
};
// response
export type UpdateAppointmentResponse = ResponseTypeAppointment;

// availability state actions
export type ProviderAvailabilityStateActions =
  | FetchProviderAvailabilitySuccessAction
  | SetProviderDetailsAction
  | UpdateFetchedProvidersAction
  | ResetProviderAvailabilityAction
  | FetchPaginatedProvidersStartAction
  | FetchPaginatedProvidersFailureAction
  | FetchPaginatedProvidersSuccessAction;

// availability actions
export type ProviderAvailabilityActions =
  | FetchProviderAvailabilityStartAction
  | FetchProviderAvailabilityFailureAction
  | FetchProviderAvailabilitySuccessAction
  | SetProviderDetailsAction
  | UpdateFetchedProvidersAction
  | ResetProviderAvailabilityAction
  | FetchPaginatedProvidersStartAction
  | FetchPaginatedProvidersFailureAction
  | FetchPaginatedProvidersSuccessAction;

// appointment state actions
export type AppointmentsStateActions =
  | FetchAllAppointmentsSuccessAction
  | ScheduleAppointmentSuccessAction
  | CancelAppointmentSuccessAction
  | UpdateAppointmentSuccessAction
  | SetSelectedAppointmentIdAction
  | ResetSelectedAppointmentIdAction
  | ResetAppointmentsStateAction
  | ResetFilters;

// appointment actions
export type AppointmentsActions =
  | FetchAllAppointmentsStartAction
  | FetchAllAppointmentsFailureAction
  | FetchAllAppointmentsSuccessAction
  | ScheduleAppointmentStartAction
  | ScheduleAppointmentFailureAction
  | ScheduleAppointmentSuccessAction
  | CancelAppointmentStartAction
  | CancelAppointmentFailureAction
  | CancelAppointmentSuccessAction
  | UpdateAppointmentStartAction
  | UpdateAppointmentSuccessAction
  | UpdateAppointmentFailureAction
  | SetSelectedAppointmentIdAction
  | ResetSelectedAppointmentIdAction
  | ResetAppointmentsStateAction
  | ResetFilters
  | ExportAppointmentsCsvStartAction
  | ExportAppointmentsCsvSuccessAction
  | ExportAppointmentsCsvFailureAction
  | ResetSpecificDaysAppointmentsStateAction;

export type ClientsActions =
  | FetchPaginatedClientsStartAction
  | FetchPaginatedClientsFailureAction
  | FetchPaginatedClientsSuccessAction
  | ResetClientsAvailability;

export type ActivityLogAction = 'created' | 'label-updated' | 'cancelled';

export enum APPOINTMENT_ACTIVITY_LOGS_SOURCE {
  MEMBER_UI = 'member-ui',
  PROVIDER_UI = 'provider-ui',
  ADMIN_UI = 'admin-ui',
  AUTOMATIC = 'automatic',
  P_VERIFY_FLOW = 'p-verify-flow',
  OPTUM = 'optum',
}

export type ActivityLogUser = {
  role: string;
  fullName?: string;
  email?: string;
};

export type ActivityLogDetails = {
  labelId?: string;
  labelName?: string;
  reasonForCancellation?: string;
  previousLabel?: string;
};

export type ActivityLog = {
  _id: string;
  calcomBookingUid: string;
  action: ActivityLogAction;
  user: ActivityLogUser;
  source: APPOINTMENT_ACTIVITY_LOGS_SOURCE;
  details?: ActivityLogDetails;
  info?: string;
  createdAt: string;
  updatedAt: string;
};

export type ActivityLogsPagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

export type ActivityLogsResponse = {
  data: ActivityLog[];
  pagination: ActivityLogsPagination;
};

export type FetchAppointmentActivityLogsStartPayload = {
  calcomBookingUid: string;
  page: number;
  limit: number;
  callback?: (data?: ActivityLogsResponse) => void;
};

export type ActivityLogsState = {
  byBookingUid: Record<string, ActivityLogsResponse>;
  isLoading: boolean;
};

export type ActivityLogsActions =
  | FetchAppointmentActivityLogsStartAction
  | FetchAppointmentActivityLogsSuccessAction
  | FetchAppointmentActivityLogsFailureAction
  | ResetAppointmentActivityLogsAction;

export enum ExportStatus {
  IDLE = 'idle',
  QUEUED = 'queued',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export type ExportProgressResponse = {
  exportId: string;
  exportStatus: ExportStatus;
  progressPercentage: number;
  totalRecords: number;
  totalChunks: number;
  completedChunks: number;
  failedChunks: number;
  processingChunks: number;
  pendingChunks: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  csvDownloadUrl?: string;
  failedReason?: string;
};

export type ExportAsyncResponse = {
  exportId: string;
};

export type ExportState = {
  exportId: string | null;
  status: ExportStatus;
  progressPercentage: number;
  totalRecords: number;
  totalChunks: number;
  completedChunks: number;
  failedChunks: number;
  csvDownloadUrl: string | null;
  failedReason: string | null;
  isPolling: boolean;
};
